import { logD, logE, logI } from "@blacknut/logging/dist";
import {
  type GamepadAxis,
  type GamepadAxisEvent,
  type GamepadButtonType,
  type GamepadTrigger,
  type GamepadTriggerEvent,
} from "@blacknut/playerwebrtc";
export interface GamepadEvent {
  type: "press" | "release" | "hold";
  button: string;
  value: number | number[];
  player: number;
}

function arraysEqual(a: unknown[], b: unknown[]) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  // If you don't care about the order of the elements inside
  // the array, you should sort both arrays here.
  // Please note that calling sort on an array will modify that array.
  // you might want to clone your array first.

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export interface GamepadHelper {
  isValidGamepad: (id: string) => Promise<boolean>;
}

declare type ConnectEventHandler = (event: {
  id?: string;
  index: number;
  hapticFeedbackCapabilities?: string[];
}) => void;

interface GamepadButtonListener {
  type: "press" | "release" | "hold";
  button: GamepadButtonType | GamepadAxis | GamepadTrigger;
  callback: (e: GamepadEvent) => void;
}

declare type InternalGamepadButtonEvent = {
  pressed: boolean;
  hold: boolean;
  released: boolean;
  player: number;
  value: number | number[];
  oldValue?: number | number[]; // see optimizeHold
};

declare type GamepadButtonMapping = Record<string, number>;

class BNGamepad {
  private hasGamepadSupport: boolean;
  private isActive = false;

  private events: {
    gamepad: Record<string, InternalGamepadButtonEvent>[];
    axes: Record<string, InternalGamepadButtonEvent>[];
  };

  private gamepadListeners: Partial<
    Record<"connect" | "disconnect", ConnectEventHandler | undefined>
  > = {};

  private keyMapping: {
    gamepad: GamepadButtonMapping;
    axes: Record<string, number[]>;
  };
  private keyMappingKeysAxes: string[];
  private keyMappingKeysButton: string[];
  private listeners: GamepadButtonListener[] = [];
  private requestAnimation?: NodeJS.Timer;

  /** if true hold event will be sent only once as long as hold does not change */
  private _optimizeHold = true;
  public get optimizeHold(): boolean {
    return this._optimizeHold;
  }
  public set optimizeHold(v: boolean) {
    this._optimizeHold = v;
  }

  private _pollingInterval: number | undefined;
  public get pollingInterval(): number | undefined {
    return this._pollingInterval;
  }
  public set pollingInterval(v: number | undefined) {
    this._pollingInterval = v;
  }

  private tag: string;

  private validGamepads: Map<string, boolean> = new Map();

  private _gamepadHelper: GamepadHelper | undefined;
  public get gamepadHelper(): GamepadHelper | undefined {
    return this._gamepadHelper;
  }
  public set gamepadHelper(v: GamepadHelper | undefined) {
    this._gamepadHelper = v;
  }

  constructor(tag: string, gamepad?: BNGamepad) {
    this.tag = tag;
    this.hasGamepadSupport = window.navigator.getGamepads !== undefined;

    this.events = gamepad?.events
      ? { ...gamepad.events }
      : {
          gamepad: [],
          axes: [],
        };

    this.keyMapping = {
      gamepad: {
        A: 0,
        B: 1,
        X: 2,
        Y: 3,
        LB: 4,
        RB: 5,
        LT: 6,
        RT: 7,
        BACK: 8,
        START: 9,
        LS: 10,
        RS: 11,
        DPU: 12,
        DPD: 13,
        DPL: 14,
        DPR: 15,
        // we do not use the 16 aka PS/XBOX button that may trigger system things on server side
        CLK: 17,
      },
      axes: {
        LA: [0, 2],
        RA: [2, 4],
      },
    };

    this.keyMappingKeysAxes = Object.keys(this.keyMapping.axes);
    this.keyMappingKeysButton = Object.keys(this.keyMapping.gamepad);

    this.listeners = [];

    this.resume();
  }

  public addConnectListener(callback: ConnectEventHandler) {
    this.gamepadListeners.connect = callback;
    this.events.gamepad = [];
  }

  public removeConnectListener() {
    delete this.gamepadListeners.connect;
  }

  public addDisconnectListener(callback: ConnectEventHandler) {
    this.gamepadListeners.disconnect = callback;
    this.events.gamepad = [];
  }

  public removeDisconnectListener() {
    delete this.gamepadListeners.disconnect;
  }

  public addInputListener(
    type: string,
    buttons:
      | GamepadButtonType
      | GamepadAxis
      | GamepadTrigger
      | (GamepadButtonType | GamepadAxis | GamepadTrigger)[],
    callback: (e: GamepadEvent | GamepadTriggerEvent | GamepadAxisEvent) => void,
  ) {
    if (typeof type === "string" && type.match(/\s+/)) {
      type.split(/\s+/g).forEach((s) => {
        this.addInputListener(s, buttons, callback);
      });
    } else if (Array.isArray(buttons) && buttons.length > 0) {
      buttons.forEach((b) => {
        this.addInputListener(type, b, callback);
      });
    } else {
      let button: GamepadButtonType | GamepadAxis | GamepadTrigger;
      if (Array.isArray(buttons)) {
        button = buttons[0];
      } else {
        button = buttons as GamepadButtonType | GamepadAxis | GamepadTrigger;
      }
      if (type === "press" || type === "release" || type === "hold") {
        this.listeners.push({
          type,
          button,
          callback,
        });
      }
    }
  }

  public removeInputListener(type: string, buttons: string | string[]) {
    if (typeof type === "string" && type.match(/\s+/)) {
      type.split(/\s+/g).forEach((s) => {
        this.removeInputListener(s, buttons);
      });
    } else if (Array.isArray(buttons) && buttons.length > 0) {
      buttons.forEach((b) => {
        this.removeInputListener(type, b);
      });
    } else {
      let button: string;
      if (Array.isArray(buttons)) {
        button = buttons[0];
      } else {
        button = buttons as string;
      }
      this.listeners = this.listeners.filter((listener) => {
        return listener.type !== type && listener.button !== button;
      });
    }
  }

  public destroy() {
    this.pause();
    this.listeners.splice(0);
  }

  private findKeyMapping(index: number, mapping: GamepadButtonMapping) {
    const results: string[] = [];
    for (let i = 0; i < this.keyMappingKeysButton.length; i++) {
      const key = this.keyMappingKeysButton[i];
      if (mapping[key] === index) {
        results.push(key);
      } /* else if (Array.isArray(mapping[key]) && mapping[key].indexOf(index) !== -1) {
                results.push(key);
            }*/
    }
    return results;
  }

  private handleGamepadConnected(
    id: string,
    index: number,
    hapticFeedbackCapabilities: string[],
  ) {
    if (this.gamepadListeners.connect) {
      this.gamepadListeners.connect({ id, index, hapticFeedbackCapabilities });
    }
  }

  private handleGamepadDisconnected(index: number) {
    if (this.gamepadListeners.disconnect) {
      this.gamepadListeners.disconnect({ index });
    }
  }

  private handleGamepadEventListener(controller: Gamepad) {
    if (controller && controller.connected) {
      for (let index = 0; index < controller.buttons.length; index++) {
        const button = controller.buttons[index];
        const keys = this.findKeyMapping(index, this.keyMapping.gamepad);
        if (keys) {
          for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (button.pressed) {
              if (!this.events.gamepad[controller.index][key]) {
                this.events.gamepad[controller.index][key] = {
                  pressed: true,
                  hold: false,
                  released: false,
                  player: controller.index,
                  value: button.value,
                };
              }
              this.events.gamepad[controller.index][key].value = button.value;
            } else if (!button.pressed && this.events.gamepad[controller.index][key]) {
              this.events.gamepad[controller.index][key].released = true;
              this.events.gamepad[controller.index][key].hold = false;
            }
          }
        }
      }
    }
  }

  private handleGamepadAxisEventListener(controller: Gamepad) {
    if (controller && controller.connected) {
      for (let i = 0; i < this.keyMappingKeysAxes.length; i++) {
        const key = this.keyMappingKeysAxes[i];

        const axes = controller.axes.slice(
          this.keyMapping.axes[key][0],
          this.keyMapping.axes[key][1],
        );
        if (axes[0] === undefined || axes[1] === undefined) {
          logD(this.tag, "Undefined axes: %o", key);
          continue;
        }
        axes[0] = Number(axes[0].toFixed(2));
        axes[1] = Number(axes[1].toFixed(2));

        // if axes has a valid value
        // Trigger event only if the value is different
        const oldX =
          this.events.axes[controller.index][key] &&
          (this.events.axes[controller.index][key]["value"] as number[])[0];
        const oldY =
          this.events.axes[controller.index][key] &&
          (this.events.axes[controller.index][key]["value"] as number[])[1];

        const tolerance = 0.1;

        let newX = axes[0];
        let forced_value = false;
        if (Math.abs(newX) < tolerance) {
          newX = 0.0;
          forced_value = true;
        }
        if (1 - Math.abs(newX) <= tolerance) {
          newX = 1 * Math.sign(newX);
          forced_value = true;
        }

        let newY = axes[1];
        if (Math.abs(newY) < tolerance) {
          newY = 0.0;
          forced_value = true;
        }
        if (1 - Math.abs(newY) <= tolerance) {
          newY = 1 * Math.sign(newY);
          forced_value = true;
        }

        if (
          !this.events.axes[controller.index][key] ||
          Math.abs(oldY - newY) > tolerance ||
          Math.abs(oldX - newX) > tolerance ||
          (forced_value && Math.abs(oldX - newX) > 0.001) || //using 0.001 to avoid to use equal on floats
          (forced_value && Math.abs(oldY - newY) > 0.001)
        ) {
          axes[0] = newX;
          axes[1] = newY;
          this.events.axes[controller.index][key] = {
            pressed: this.events.axes[controller.index][key] ? false : true,
            hold: this.events.axes[controller.index][key] ? true : false,
            released: false,
            value: axes,
            player: controller.index,
          };
        }
      }
    }
  }

  private handleEvent(
    key: string,
    events: Record<string, InternalGamepadButtonEvent>,
    player: number,
  ) {
    if (events[key].pressed) {
      this.trigger("press", key, events[key].value, player);
      events[key].pressed = false;
      events[key].hold = true;
      events[key].oldValue = events[key].value;
    } else if (events[key].hold) {
      if (
        this._optimizeHold &&
        typeof events[key].value === "number" &&
        (events[key].oldValue === events[key].value && events[key].player) === player &&
        events[key].player === player
      ) {
        //console.log("IGNORE !!", player, events[key].oldValue, events[key].value);
        return;
      }
      if (
        this._optimizeHold &&
        Array.isArray(events[key].value) &&
        events[key].oldValue &&
        arraysEqual(events[key].oldValue as number[], events[key].value as number[]) &&
        events[key].player === player
      ) {
        //console.log("IGNORE !!", events[key].oldValue, events[key].value);
        return;
      }
      events[key].oldValue = events[key].value;
      this.trigger("hold", key, events[key].value, player);
    } else if (events[key].released) {
      this.trigger("release", key, events[key].value, player);
      delete events[key];
    }
  }

  private loop() {
    // redo
    this.getGamepads().finally(() => {
      this.requestAnimation = setTimeout(
        this.loop.bind(this),
        this.pollingInterval ? this.pollingInterval : 0,
      );
    });
  }

  private isValidGamepad(gamepad: Gamepad) {
    if (!this.validGamepads.has(gamepad.id) && this.gamepadHelper) {
      return this.gamepadHelper
        .isValidGamepad(gamepad.id)
        .then((res) => {
          logI(this.tag, "isValidGamepad: got value %o for is: %o", res, gamepad.id);
          this.validGamepads.set(gamepad.id, res);
          return res;
        })
        .catch((e) => {
          logE(this.tag, "Caught error getting native gamepad state: %e", e);
          return true;
        });
    } else if (this.validGamepads.has(gamepad.id)) {
      return Promise.resolve(!!this.validGamepads.get(gamepad.id));
    }
    return Promise.resolve(gamepad.id?.indexOf("JIO BLE Remote") === -1);
  }

  private getValidGamepads() {
    const navigatorGetGamepads = this.hasGamepadSupport
      ? window.navigator.getGamepads()
      : [];
    if (!navigatorGetGamepads) return Promise.resolve([] as Gamepad[]);
    const gamepads: Gamepad[] = [];
    // no .filter().map() since 'typeof window.navigator.getGamepads()' can be 'object'
    for (let i = 0; i < navigatorGetGamepads.length; i = i + 1) {
      const gamepad = navigatorGetGamepads[i];
      if (!gamepad) continue;
      const gamepadTemp: Gamepad = {
        axes: navigatorGetGamepads[i]?.axes || [],
        buttons: navigatorGetGamepads[i]?.buttons || [],
        connected: !!navigatorGetGamepads[i]?.connected,
        hapticActuators: navigatorGetGamepads[i]?.hapticActuators || [],
        id: navigatorGetGamepads[i]?.id || "", // Fix Jio CA-4698
        index: gamepads.length,
        mapping: navigatorGetGamepads[i]?.mapping || "",
        timestamp: navigatorGetGamepads[i]?.timestamp || 0,
        vibrationActuator: navigatorGetGamepads[i]?.vibrationActuator || null,
      };
      gamepads.push(gamepadTemp);
    }
    return Promise.all(gamepads.map((e) => this.isValidGamepad(e))).then(
      (validStatuses) => {
        return gamepads
          .filter((e, i) => {
            return validStatuses[i];
          })
          .map((e, i) => ({ ...e, index: i }));
      },
    );
  }

  private processGamepads(gamepads: Gamepad[]) {
    const length = gamepads.length;
    if (gamepads && length) {
      // Add or delete events of gamepad connected/deconnected
      for (let i = 0; i < length; i = i + 1) {
        if (gamepads[i]) {
          if (!this.events.gamepad[i]) {
            const hapticFeedbackCapabilities: string[] = [];
            const actuator = gamepads[i]?.vibrationActuator;
            if (actuator) {
              hapticFeedbackCapabilities.push(actuator.type);
            }
            const actuators = gamepads[i]?.hapticActuators;
            if (actuators) {
              for (let i = 0; i < actuators.length; i++) {
                if (!hapticFeedbackCapabilities.includes(actuators[i].type)) {
                  hapticFeedbackCapabilities.push(actuators[i].type);
                }
              }
            }
            this.handleGamepadConnected(
              gamepads[i]?.id || "",
              i,
              hapticFeedbackCapabilities,
            );
            this.events.gamepad[i] = {};
            this.events.axes[i] = {};
          }

          this.handleGamepadEventListener(gamepads[i] as Gamepad);
          this.handleGamepadAxisEventListener(gamepads[i] as Gamepad);
        } else if (this.events.gamepad[i]) {
          this.handleGamepadDisconnected(i);
          delete this.events.gamepad[i];
          delete this.events.axes[i];
        }
      }

      // handle button events of gamepad connected
      for (let player = 0; player < this.events.gamepad.length; player++) {
        const gamepadInstance = this.events.gamepad[player];
        if (gamepadInstance) {
          const keys = Object.keys(gamepadInstance);
          for (let index = 0; index < keys.length; index++) {
            const key = keys[index];
            this.handleEvent(key, gamepadInstance, player);
          }
        }
      }

      // handle axes events of gamepad connected
      for (let player = 0; player < this.events.axes.length; player++) {
        const gamepadInstance = this.events.axes[player];
        if (gamepadInstance) {
          const keys = Object.keys(gamepadInstance);
          for (let index = 0; index < keys.length; index++) {
            const key = keys[index];
            this.handleEvent(key, gamepadInstance, player);
          }
        }
      }
    }
  }
  private getGamepads() {
    //console.log(`${this.tag} - polling`, new Date());

    return this.getValidGamepads()
      .then((gamepads) => {
        this.processGamepads(gamepads);
      })
      .catch((e) => {
        logE(this.tag, "Error processing gamepads: %o", e);
      });
  }

  private trigger(
    type: string,
    button: string,
    value: number | number[],
    player: number,
  ) {
    if (this.listeners) {
      for (let i = 0; i < this.listeners.length; i++) {
        const listener = this.listeners[i];
        if (listener.type === type && listener.button === button) {
          const event: GamepadEvent = {
            type: listener.type,
            button: listener.button,
            value,
            player,
          };
          listener.callback(event);
        }
      }
    }
  }

  public pause() {
    logD(this.tag, "pause");

    if (this.isActive) {
      this.isActive = false;
      if (this.requestAnimation !== undefined) {
        clearTimeout(this.requestAnimation);
        this.requestAnimation = undefined;
      }
    }
    window.removeEventListener("gamepadconnected", this._onGamepadConnected);
  }

  public resume() {
    logD(this.tag, "resume");
    if (!this.isActive) {
      this.requestAnimation = setTimeout(
        this.loop.bind(this),
        this.pollingInterval ? this.pollingInterval : 0,
      );
      this.isActive = true;
    }
    // this is required on smg: otherwise getGamepads entry [null] list
    window.addEventListener("gamepadconnected", this._onGamepadConnected);
  }

  private _onGamepadConnected(e: any) {
    logD(this.tag, "Gamepad connected: %o", e.gamepad);
  }
}

export default BNGamepad;
